import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { apiService } from '@/services/api';
import { DrinkOut, FoodOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import StockIndicator from '@/components/atoms/StockIndicator/StockIndicator';
import './BarmanStock.scss';

const BarmanStock: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { notifications } = useNotifications();

  const eventId = user?.event_id ?? null;
  const venueId = user?.event_venue_id ?? null;

  const [drinks, setDrinks] = useState<DrinkOut[]>([]);
  const [foods, setFoods] = useState<FoodOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStock = useCallback(async () => {
    if (!eventId || !venueId) {
      setLoading(false);
      return;
    }
    try {
      const [drinksData, foodsData] = await Promise.all([
        apiService.getDrinks({ event_id: eventId, event_venue_id: venueId }),
        apiService.getFoods({ event_id: eventId, event_venue_id: venueId }),
      ]);
      setDrinks(drinksData);
      setFoods(foodsData);
    } catch (err) {
      console.error('Failed to load bar stock:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [eventId, venueId, t]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  // Cheap "soft real-time" refresh: refetch whenever a new notification arrives
  // (covers low-stock alerts for this bar, delivered over the existing WS pipeline).
  useEffect(() => {
    loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length]);

  const lowStockAlerts = notifications.filter((n) => n.notification_type?.name === 'bar_low_stock');

  if (!eventId || !venueId) {
    return (
      <div className="barman-stock">
        <Card variant="elevated">
          <p>{t('bartender.dashboard.noVenueSelected') || t('warehouse.dashboard.noEventAssigned')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="barman-stock">
      <h2 className="barman-stock__title">{t('barmanStock.title', { defaultValue: 'Stock de tu barra' })}</h2>

      {error && <div className="barman-stock__error">{error}</div>}

      {lowStockAlerts.length > 0 && (
        <Card variant="elevated" className="barman-stock__alerts">
          <h3>{t('barmanStock.alertsTitle', { defaultValue: 'Alertas de stock' })}</h3>
          <ul className="barman-stock__alerts-list">
            {lowStockAlerts.slice(0, 10).map((n) => (
              <li key={n.id} className={!n.read ? 'barman-stock__alert--unread' : ''}>
                {n.message}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div className="barman-stock__products">
          {drinks.map((drink) => (
            <Card key={`drink-${drink.id}`} variant="elevated" className="barman-stock__product">
              <span className="barman-stock__product-name">{drink.name}</span>
              <StockIndicator
                stockQuantity={drink.stock_quantity}
                maxStock={drink.max_stock}
                minStockThreshold={drink.min_stock_threshold}
                isAvailable={drink.is_available}
                showDetails
              />
              <span className="barman-stock__product-qty">{drink.stock_quantity}</span>
            </Card>
          ))}
          {foods.map((food) => (
            <Card key={`food-${food.id}`} variant="elevated" className="barman-stock__product">
              <span className="barman-stock__product-name">{food.name}</span>
              <StockIndicator
                stockQuantity={food.stock_quantity}
                maxStock={food.max_stock}
                minStockThreshold={food.min_stock_threshold}
                isAvailable={food.is_available}
                showDetails
              />
              <span className="barman-stock__product-qty">{food.stock_quantity}</span>
            </Card>
          ))}
          {drinks.length === 0 && foods.length === 0 && (
            <Card variant="elevated">
              <p>{t('barmanStock.empty', { defaultValue: 'Tu barra todavía no tiene productos.' })}</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default BarmanStock;
